import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';

import { FormFolderDoc } from '@vyce/core/src/api/types';
import { getPaddingForContent } from '@vyce/core/src/utils';
import {
  buildFormUrlSegment,
  formTitleSlugToDisplayName,
  parseFormUrlSegment,
} from '@vyce/core/src/utils/formUrl';
import { DeviceContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { FormDoc, FormRecipientsDialogType, User } from '@vyce/core/src/types';
import { ConfirmDialog } from '@vyce/core/src/components';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { SafeDndProvider } from '@vyce/core/src/components/SafeDndProvider';
import { useDebounceValue } from '@vyce/core/src/hooks';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  addFormToFolderRequest,
  archiveFormFolderRequest,
  createFormFolderRequest,
  deleteFormFolderRequest,
  getFormFoldersRequest,
  getFormsRequest,
  moveFormFolderRequest,
  sendFormMessageRequest,
  updateFormFolderRequest,
  updateFormStatusRequest,
} from '@vyce/core/src/api/forms';
import { useCreateFormData } from './hooks/useCreateFormData';
import { useFormRecipients } from './hooks/useFormRecipients';
import { FormSendDialog } from './components/FormSendDialog';
import { FormAIAnalyticsDialog } from './components/FormAIAnalyticsDialog';
import { FormRecipientsDialog } from './components/FormRecipientsDialog';
import { FolderCard } from './components/FolderCard';
import { FolderCardSkeleton } from './components/FolderCardSkeleton';
import { CreateFolderDialog } from './components/CreateFolderDialog';
import { ChangeFolderDialog } from './components/ChangeFolderDialog';
import { MoveFolderDialog } from './components/MoveFolderDialog';
import { RenameFolderDialog } from './components/RenameFolderDialog';
import { FormsDataGrid } from './components/FormsDataGrid';
import { MyFormsToolbar } from './components/MyFormsToolbar';

const FORM_DRAG_TYPE = 'FORM_UNASSIGNED_CARD';
const FORM_FOLDERS_FETCH_LIMIT = 500;
const SEARCH_DEBOUNCE_MS = 400;

export const FolderFormsPage: React.FC<{ user: User; companyId: string }> = ({ user, companyId }) => {
  const { folderSlug } = useParams<{ folderSlug: string }>();
  const history = useHistory();
  const location = useLocation();
  const isFolderView = new URLSearchParams(location.search).get('view') !== 'list';
  const { isMobile } = useContext(DeviceContext);
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const showNotificationRef = useRef(showNotification);
  const handleServerErrorRef = useRef(handleServerError);
  const padding = getPaddingForContent(isMobile);

  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [folders, setFolders] = useState<FormFolderDoc[]>([]);
  const [forms, setForms] = useState<FormDoc[]>([]);
  const [total, setTotal] = useState(0);
  const [folderSearch, setFolderSearch] = useState('');
  const debouncedFolderSearch = useDebounceValue(folderSearch, SEARCH_DEBOUNCE_MS);
  const folderSearchForApi = debouncedFolderSearch === null ? '' : debouncedFolderSearch.trim();
  const [formsSearch, setFormsSearch] = useState('');
  const debouncedFormsSearch = useDebounceValue(formsSearch, SEARCH_DEBOUNCE_MS);
  const formsSearchForApi = debouncedFormsSearch === null ? '' : debouncedFormsSearch.trim();
  const [isArchivedView, setIsArchivedView] = useState(false);
  const statusFilter = isArchivedView ? 'archived' : undefined;
  const folderPathSegments = useMemo(() => {
    if (!folderSlug) return [];
    return String(folderSlug)
      .split('/')
      .map(segment => segment.trim())
      .filter(Boolean);
  }, [folderSlug]);

  const currentFolderSlug = folderPathSegments[folderPathSegments.length - 1] || '';
  const parsedFolder = useMemo(() => parseFormUrlSegment(currentFolderSlug), [currentFolderSlug]);
  const folderId = parsedFolder.id;
  const folderTitleFromPath = useMemo(() => {
    if (!parsedFolder.titleSlug) return '';
    const { titleSlug } = parsedFolder;
    return formTitleSlugToDisplayName(titleSlug);
  }, [parsedFolder]);
  const parentFolderName = useMemo(() => {
    if (folderPathSegments.length < 2) return 'Root';
    const parentSegment = folderPathSegments[folderPathSegments.length - 2];
    const parsedParent = parseFormUrlSegment(parentSegment);
    if (!parsedParent.titleSlug) return 'Root';
    return formTitleSlugToDisplayName(parsedParent.titleSlug);
  }, [folderPathSegments]);

  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [openDeleteFolderDialog, setOpenDeleteFolderDialog] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<FormFolderDoc | null>(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [openMoveFolderDialog, setOpenMoveFolderDialog] = useState(false);
  const [movingFolder, setMovingFolder] = useState(false);
  const [openRenameFolderDialog, setOpenRenameFolderDialog] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(false);
  const [openDeleteFormDialog, setOpenDeleteFormDialog] = useState<boolean>(false);
  const [openChangeFolderDialog, setOpenChangeFolderDialog] = useState<boolean>(false);
  const [openArchiveFormDialog, setOpenArchiveFormDialog] = useState<boolean>(false);
  const [loadingArchiveForm, setLoadingArchiveForm] = useState<boolean>(false);
  const [openSendFormDialog, setOpenSendFormDialog] = useState<boolean>(false);
  const [selectedFormId, setSelectedFormId] = useState<string | undefined>();
  const [loadingChangeFolder, setLoadingChangeFolder] = useState<boolean>(false);
  const [openAIAnalyticsDialog, setOpenAIAnalyticsDialog] = useState<boolean>(false);
  const [selectedFormForAIAnalytics, setSelectedFormForAIAnalytics] = useState<FormDoc | null>(null);
  const [openResendAllDialog, setOpenResendAllDialog] = useState<boolean>(false);
  const [loadingResendAll, setLoadingResendAll] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<FormRecipientsDialogType>('recipients');
  const [openRecipientsDialog, setOpenRecipientsDialog] = useState<boolean>(false);
  const { deleteForm, duplicateForm, loading: loadingDeleteForm } = useCreateFormData({ companyId, user });
  const [loadingSendForm, setLoadingSendForm] = useState<boolean>(false);
  const {
    isPhoneInputDisabled,
    handleEmailFieldKeyDown,
    handlePhoneFieldKeyDown,
    addEmailToList,
    addPhoneToList,
    deleteContact,
    emails,
    phones,
    setIsPhoneInputDisabled,
    bulkPaste,
    bulkLimitHint,
    recipientsFormMethods,
    sendFormRecipients,
  } = useFormRecipients({ user, companyId });

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    handleServerErrorRef.current = handleServerError;
  }, [handleServerError]);

  const loadFolders = useCallback(async () => {
    if (!companyId || !folderId) return;
    setLoadingFolders(true);
    try {
      const { folders: children } = await getFormFoldersRequest({
        companyId,
        userId: user.uuid,
        parentId: folderId,
        limit: FORM_FOLDERS_FETCH_LIMIT,
        search: folderSearchForApi || null,
        archived: isArchivedView,
      });
      setFolders(children);
    } catch (error) {
      handleServerErrorRef.current(error);
    } finally {
      setLoadingFolders(false);
    }
  }, [companyId, folderId, user.uuid, folderSearchForApi, isArchivedView]);

  const loadForms = useCallback(async () => {
    if (!companyId || !folderId) return;
    setLoadingForms(true);
    try {
      const formsResponse = await getFormsRequest({
        offset: 0,
        limit: 100,
        substring: '',
        search: formsSearchForApi || undefined,
        companyId,
        userId: user.uuid,
        folderId,
        includeDescendants: false,
        status: statusFilter,
      });
      setForms(formsResponse.forms || []);
      setTotal(formsResponse.total || 0);
    } catch (error) {
      handleServerErrorRef.current(error);
    } finally {
      setLoadingForms(false);
    }
  }, [companyId, folderId, user.uuid, formsSearchForApi, statusFilter]);

  const loadFolderPage = useCallback(async () => {
    await Promise.all([loadFolders(), loadForms()]);
  }, [loadFolders, loadForms]);

  useEffect(() => {
    void loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    void loadForms();
  }, [loadForms]);

  const handleSendFormRecipients = async () => {
    if (!selectedFormId) return;
    setLoadingSendForm(true);
    await sendFormRecipients(selectedFormId);
    setOpenSendFormDialog(false);
    setLoadingSendForm(false);
  };

  const handleResendAll = async () => {
    if (!selectedFormId) return;
    try {
      setLoadingResendAll(true);
      await sendFormMessageRequest({
        companyId,
        formId: selectedFormId,
        userId: user.uuid,
        sendToAll: true,
      });
      showNotificationRef.current({ message: 'Form has been sent', options: { variant: 'success' } });
      setSelectedFormId(undefined);
      setOpenResendAllDialog(false);
    } catch (error) {
      handleServerErrorRef.current(error);
    } finally {
      setLoadingResendAll(false);
    }
  };

  const handleDropToSubfolder = useCallback(
    async (form: FormDoc, targetFolderId: string) => {
      if (!form.id || !targetFolderId) return;
      const previousForms = [...forms];
      setForms(prev => prev.filter(item => item.id !== form.id));
      try {
        await addFormToFolderRequest({
          companyId,
          formId: form.id,
          folderId: targetFolderId,
          userId: user.uuid,
        });
        showNotificationRef.current({
          message: 'Form moved to another folder',
          options: { variant: 'success' },
        });
        await loadFolderPage();
      } catch (error) {
        setForms(previousForms);
        handleServerErrorRef.current(error);
      }
    },
    [companyId, forms, loadFolderPage, user.uuid]
  );

  return (
    <Box padding={padding}>
      <Box marginBottom={2} display="flex" alignItems="center" gridGap={16}>
        <Typography variant="h6" noWrap>
          {folderTitleFromPath || parentFolderName}
        </Typography>
        <Box flexGrow={1} minWidth={0}>
          <MyFormsToolbar
            isFolderView={isFolderView}
            isArchivedView={isArchivedView}
            isArchivedFoldersView={isArchivedView}
            folders={[]}
            showFolderFilter={false}
            createItemFolderLabel="Create subfolder"
            itemSearchPlaceholder="Search forms..."
            onSearchChange={e => setFormsSearch(e?.target?.value || '')}
            onFolderSearchChange={value => setFolderSearch(value)}
            onFolderFilterChange={() => null}
            onArchivedViewChange={setIsArchivedView}
            onArchivedFoldersViewChange={setIsArchivedView}
            onFolderViewChange={checked => {
              history.push(checked ? location.pathname : `${location.pathname}?view=list`);
            }}
            onCreateFolder={() => setOpenCreateFolderDialog(true)}
            onCreateForm={() => history.push(`/forms/new-form?folderId=${folderId}`)}
          />
        </Box>
      </Box>

      <SafeDndProvider backend={HTML5Backend}>
        {isFolderView && (
          <>
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
          gridGap={16}
          marginBottom={3}
        >
          {loadingFolders
            ? Array.from({ length: 3 }, (_, i) => <FolderCardSkeleton key={`child-folder-skeleton-${i}`} />)
            : folders.map(folder => (
                <FolderCard
                  key={folder.id}
                  folder={folder}
                  dragType={FORM_DRAG_TYPE}
                  onDrop={handleDropToSubfolder}
                  onDeleteFolder={childFolderId => {
                    setSelectedFolderId(childFolderId);
                    setOpenDeleteFolderDialog(true);
                  }}
                  onMoveFolderToFolder={async (sourceFolderId, targetFolderId) => {
                    setLoadingFolders(true);
                    try {
                      await moveFormFolderRequest({
                        companyId,
                        folderId: sourceFolderId,
                        newParentId: targetFolderId,
                        userId: user.uuid,
                      });
                      showNotificationRef.current({
                        message: 'Folder moved',
                        options: { variant: 'success' },
                      });
                      await loadFolderPage();
                    } catch (error) {
                      handleServerErrorRef.current(error);
                      setLoadingFolders(false);
                    }
                  }}
                  onMoveFolder={targetFolder => {
                    setSelectedFolder(targetFolder);
                    setOpenMoveFolderDialog(true);
                  }}
                  onRenameFolder={targetFolder => {
                    setSelectedFolder(targetFolder);
                    setOpenRenameFolderDialog(true);
                  }}
                  onArchiveFolder={async targetFolder => {
                    try {
                      await archiveFormFolderRequest({
                        companyId,
                        folderId: targetFolder.id,
                        userId: user.uuid,
                        archived: targetFolder.archived !== true,
                      });
                      showNotificationRef.current({
                        message: targetFolder.archived !== true
                          ? 'Folder archived'
                          : 'Folder unarchived',
                        options: { variant: 'success' },
                      });
                      await loadFolderPage();
                    } catch (error) {
                      handleServerErrorRef.current(error);
                    }
                  }}
                  onColourFolder={async (childId, colour) => {
                    const previousColour = folders.find(f => f.id === childId)?.colour ?? null;
                    setFolders(prev =>
                      prev.map(f => (f.id === childId ? { ...f, colour } : f))
                    );
                    try {
                      await updateFormFolderRequest({
                        companyId,
                        folderId: childId,
                        userId: user.uuid,
                        colour,
                      });
                    } catch (error) {
                      setFolders(prev =>
                        prev.map(f => (f.id === childId ? { ...f, colour: previousColour } : f))
                      );
                      handleServerErrorRef.current(error);
                    }
                  }}
                  onOpenFolder={() =>
                    history.push(
                      `/forms/my-forms/folders/${[
                        ...folderPathSegments,
                        buildFormUrlSegment(folder.name, folder.id),
                      ].join('/')}`
                    )
                  }
                />
              ))}
          {!loadingFolders && folders.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No Folders
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gridGap={16} marginBottom={1}>
          <Typography variant="h6">
            Forms in folder ({total})
          </Typography>
          <AppSearchInput
            placeholder="Search forms..."
            value={formsSearch}
            onChange={e => setFormsSearch(e.target.value)}
            isSmall
            smallRounding
            isBorder
            expanded={true}
          />
        </Box>
          </>
        )}
        <FormsDataGrid
          rows={forms}
          enableDragAndDrop
          dragType={FORM_DRAG_TYPE}
          withActions
          onEditForm={form =>
            history.push(`/forms/my-forms/${buildFormUrlSegment(form.title || '', form.id || '')}`)
          }
          onPreviewForm={form =>
            history.push(
              `/forms/my-forms/${buildFormUrlSegment(form.title || '', form.id || '')}?preview=true`
            )
          }
          onViewAnswers={form =>
            history.push(
              `/forms/form-answers/${buildFormUrlSegment(form.title || '', form.id || '')}/summary`
            )
          }
          onDuplicateForm={async form => {
            const newFormId = await duplicateForm(form.id || '');
            if (newFormId)
              history.push(`/forms/my-forms/${buildFormUrlSegment(form.title || '', newFormId)}`);
            await loadFolderPage();
          }}
          onSettingsForm={form =>
            history.push(
              `/forms/form-answers/${buildFormUrlSegment(form.title || '', form.id || '')}/settings`
            )
          }
          onSendForm={form => {
            setSelectedFormId(form.id);
            setOpenSendFormDialog(true);
          }}
          onArchiveForm={form => {
            setSelectedFormId(form.id);
            setOpenArchiveFormDialog(true);
          }}
          onDeleteForm={form => {
            setSelectedFormId(form.id);
            setOpenDeleteFormDialog(true);
          }}
          onChangeFolder={form => {
            setSelectedFormId(form.id);
            setOpenChangeFolderDialog(true);
          }}
          onViewOutstanding={form => {
            setSelectedFormId(form.id);
            setDialogType('outstanding');
            setOpenRecipientsDialog(true);
          }}
          onViewParticipants={(form, type) => {
            setSelectedFormId(form.id);
            setDialogType(type);
            setOpenRecipientsDialog(true);
          }}
          onResendAll={form => {
            setSelectedFormId(form.id);
            setOpenResendAllDialog(true);
          }}
          onOpenAIAnalytics={form => {
            setSelectedFormForAIAnalytics(form);
            setOpenAIAnalyticsDialog(true);
          }}
          height="60vh"
          loading={loadingForms}
          total={total}
          setOffset={() => null}
        />
      </SafeDndProvider>

      <CreateFolderDialog
        open={openCreateFolderDialog}
        folderName={newFolderName}
        loading={creatingFolder}
        onFolderNameChange={setNewFolderName}
        onClose={() => setOpenCreateFolderDialog(false)}
        onCreate={async colour => {
          if (!newFolderName.trim()) return;
          try {
            setCreatingFolder(true);
            await createFormFolderRequest({
              companyId,
              userId: user.uuid,
              name: newFolderName.trim(),
              parentId: folderId,
              colour,
            });
            setNewFolderName('');
            setOpenCreateFolderDialog(false);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setCreatingFolder(false);
          }
        }}
      />

      <ConfirmDialog
        open={openArchiveFormDialog}
        handleClose={() => setOpenArchiveFormDialog(false)}
        handleConfirm={async () => {
          if (!selectedFormId) return;
          try {
            setLoadingArchiveForm(true);
            await updateFormStatusRequest({
              companyId,
              formId: selectedFormId,
              userId: user.uuid,
              status: 'archived',
            });
            setOpenArchiveFormDialog(false);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setLoadingArchiveForm(false);
          }
        }}
        title="Are you sure you want to archive this form?"
        loading={loadingArchiveForm}
        confirmText="Archive"
        cancelText="Cancel"
      />

      <ConfirmDialog
        open={openDeleteFormDialog}
        handleClose={() => setOpenDeleteFormDialog(false)}
        handleConfirm={async () => {
          await deleteForm(selectedFormId || '');
          setOpenDeleteFormDialog(false);
          await loadFolderPage();
        }}
        title="Are you sure you want to delete this form?"
        loading={loadingDeleteForm}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ConfirmDialog
        open={openDeleteFolderDialog}
        handleClose={() => {
          setOpenDeleteFolderDialog(false);
          setSelectedFolderId(null);
        }}
        handleConfirm={async () => {
          if (!selectedFolderId) return;
          try {
            setDeletingFolder(true);
            await deleteFormFolderRequest({
              companyId,
              folderId: selectedFolderId,
              userId: user.uuid,
              mode: 'cascade',
            });
            setOpenDeleteFolderDialog(false);
            setSelectedFolderId(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setDeletingFolder(false);
          }
        }}
        title="Are you sure you want to delete this folder?"
        subtitle="All nested folders inside this folder will also be deleted."
        loading={deletingFolder}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <MoveFolderDialog
        open={openMoveFolderDialog}
        loading={movingFolder}
        companyId={companyId}
        userId={user.uuid}
        folderId={selectedFolder?.id ?? null}
        currentParentId={selectedFolder?.parentId ?? null}
        onClose={() => {
          setOpenMoveFolderDialog(false);
          setSelectedFolder(null);
        }}
        onConfirm={async newParentId => {
          if (!selectedFolder) return;
          try {
            setMovingFolder(true);
            await moveFormFolderRequest({
              companyId,
              folderId: selectedFolder.id,
              newParentId,
              userId: user.uuid,
            });
            showNotificationRef.current({ message: 'Folder moved', options: { variant: 'success' } });
            setOpenMoveFolderDialog(false);
            setSelectedFolder(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setMovingFolder(false);
          }
        }}
      />

      <RenameFolderDialog
        open={openRenameFolderDialog}
        initialName={selectedFolder?.name ?? ''}
        loading={renamingFolder}
        onClose={() => {
          setOpenRenameFolderDialog(false);
          setSelectedFolder(null);
        }}
        onConfirm={async name => {
          if (!selectedFolder) return;
          try {
            setRenamingFolder(true);
            await updateFormFolderRequest({
              companyId,
              folderId: selectedFolder.id,
              userId: user.uuid,
              name,
            });
            showNotificationRef.current({ message: 'Folder renamed', options: { variant: 'success' } });
            setOpenRenameFolderDialog(false);
            setSelectedFolder(null);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setRenamingFolder(false);
          }
        }}
      />

      <ChangeFolderDialog
        open={openChangeFolderDialog}
        loading={loadingChangeFolder}
        companyId={companyId}
        userId={user.uuid}
        onClose={() => {
          setOpenChangeFolderDialog(false);
          setSelectedFormId(undefined);
        }}
        onConfirm={async selectedTargetFolderId => {
          if (!selectedFormId || !selectedTargetFolderId) return;
          try {
            setLoadingChangeFolder(true);
            await addFormToFolderRequest({
              companyId,
              formId: selectedFormId,
              folderId: selectedTargetFolderId,
              userId: user.uuid,
            });
            showNotificationRef.current({
              message: 'Form moved to another folder',
              options: { variant: 'success' },
            });
            setOpenChangeFolderDialog(false);
            setSelectedFormId(undefined);
            await loadFolderPage();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setLoadingChangeFolder(false);
          }
        }}
      />

      <FormProvider {...recipientsFormMethods}>
        <form>
          <FormSendDialog
            open={openSendFormDialog}
            onClose={() => setOpenSendFormDialog(false)}
            isPhoneInputDisabled={isPhoneInputDisabled}
            handleEmailFieldKeyDown={handleEmailFieldKeyDown}
            handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
            addEmailToList={addEmailToList}
            addPhoneToList={addPhoneToList}
            deleteContact={deleteContact}
            emails={emails}
            phones={phones}
            setIsPhoneInputDisabled={setIsPhoneInputDisabled}
            bulkPaste={bulkPaste}
            bulkLimitHint={bulkLimitHint}
            sendFormRecipients={handleSendFormRecipients}
            loading={loadingSendForm}
          />
        </form>
      </FormProvider>

      <FormAIAnalyticsDialog
        open={openAIAnalyticsDialog}
        onClose={() => {
          setOpenAIAnalyticsDialog(false);
          setSelectedFormForAIAnalytics(null);
        }}
        selectedFormForAIAnalytics={selectedFormForAIAnalytics}
        user={user}
        companyId={companyId}
      />

      <ConfirmDialog
        open={openResendAllDialog}
        handleClose={() => setOpenResendAllDialog(false)}
        handleConfirm={handleResendAll}
        loading={loadingResendAll}
        confirmText="Resend"
        cancelText="Cancel"
        title="Are you sure you want to resend all messages to all recipients, who haven't filled the form yet?"
      />

      {selectedFormId && (
        <FormRecipientsDialog
          open={openRecipientsDialog}
          onClose={() => {
            setOpenRecipientsDialog(false);
          }}
          type={dialogType}
          companyId={companyId}
          formId={selectedFormId}
          user={user}
        />
      )}
    </Box>
  );
};
